<script lang="ts" setup>
	import type { RuleObject } from 'ant-design-vue/es/form'
	import { useResourcesApi } from '~/services/apis/resources'
	type NinPayload = {
		first_name?: string
		last_name?: string
		email: string
	}

	const {
		required,
		policy,
		validateOnUrl = true
	} = defineProps<{
		warningText?: string
		required?: boolean
		validateOnUrl?: boolean
		policy?: PolicyResponse | null
	}>()
	const payload = computed(() => ({
		first_name: policy?.first_name || '',
		last_name: policy?.last_name || '',
		email: policy?.email || ''
	}))
	const modelVal = defineModel<number | string>({ default: '' })
	const validatingNin = defineModel<boolean>('validating', { default: false, required: false })
	const handleNinValidation = async (rule: RuleObject, value: string) => {
		// This is because this is made (optionsal)
		if (required && !value) {
			return Promise.reject(new Error('NIN is required'))
		}

		if (String(value).length < 11 || String(value).length > 11) {
			return Promise.reject(new Error('NIN must be 11 characters long'))
		}
		if (!String(value).match(/^[0-9]+$/)) {
			return Promise.reject(new Error('NIN must be a number'))
		}

		return Promise.resolve()
	}
	const { validateNINNumber } = useResourcesApi()
	const validateNin = (fn?: (value: boolean) => void) => {
		if (String(modelVal.value).length === 11) {
			if (!validateOnUrl) {
				fn?.(true)
				return true
			}
			return resolve(async () => {
				const res = await validateNINNumber(String(modelVal.value), payload.value)
				if (res?.data?.meta?.nin_verified) {
					fn?.(true)
					return true
				}
				fn?.(false)
				return false
			}, validatingNin)
		}
		fn?.(false)
		return Promise.resolve(false)
	}
	defineExpose({ validateNin })
</script>

<template>
	<div>
		<a-form-item
			label="National Identity Number (NIN) "
			name="nin"
			:rules="{ validator: handleNinValidation, trigger: 'blur' }"
		>
			<a-input
				v-model:value="modelVal"
				placeholder="Enter your staff national identification number"
				v-number="true"
				:maxlength="11"
				:disabled="validatingNin"
				data-testid="nin"
				class="custom-input w-full !bg-gray-50 !px-5"
			>
				<template #suffix>
					<tools-icon v-if="validatingNin" icon="eclipse" dir="line" />
				</template>
			</a-input>
			<p class="pt-[8px] text-xs font-medium text-warning-500">
				{{ warningText || 'Request for the staff NIN to enable verification (optional)' }}
			</p>
		</a-form-item>
	</div>
</template>
